/**
 * Limits and guards both sides apply.
 *
 * The client checks these to give immediate feedback; the server checks them
 * again because a client is just a socket frame and can lie. Sharing the
 * constants means the two can't drift into disagreeing about what's valid.
 */

/**
 * Room ids are typed by hand from a shared screen, so the alphabet drops the
 * characters people misread: 0/O, 1/I/L.
 */
export const ROOM_ID_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const ROOM_ID_LENGTH = 6;

export const MIN_NAME_LENGTH = 1;
export const MAX_NAME_LENGTH = 32;
export const MAX_ROOM_NAME_LENGTH = 60;
export const MAX_STORY_TITLE_LENGTH = 120;
export const MAX_STORY_DESCRIPTION_LENGTH = 1000;
export const MAX_PARTICIPANTS_PER_ROOM = 50;

/**
 * Bounds on a round timer. The floor keeps a fat-fingered "1" from revealing
 * before anyone can read the story; the ceiling stops a stray large number
 * from pinning a `setTimeout` open for the life of the dyno.
 */
export const MIN_TIMER_SECONDS = 10;
export const MAX_TIMER_SECONDS = 60;

const ROOM_ID_PATTERN = new RegExp(`^[${ROOM_ID_ALPHABET}]{${ROOM_ID_LENGTH}}$`);

export function isValidRoomId(value: string): boolean {
  return ROOM_ID_PATTERN.test(value);
}

/**
 * Collapses whitespace and trims. Display names go through this before any
 * length check so " Ada  Lovelace " and "Ada Lovelace" are the same person —
 * names are how a reconnecting participant is matched back to their seat.
 */
export function normalizeName(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function isValidParticipantName(value: string): boolean {
  const normalized = normalizeName(value);
  return normalized.length >= MIN_NAME_LENGTH && normalized.length <= MAX_NAME_LENGTH;
}

/**
 * Coerces a requested round timer into a whole number of seconds within range,
 * or `null` when there is no usable timer. Anything not a finite number, or
 * below the floor, disables the timer rather than guessing; values above the
 * ceiling are clamped down. Shared so the client's input and the server's guard
 * agree on what a valid duration is.
 */
export function normalizeTimerSeconds(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.round(value);
  if (rounded < MIN_TIMER_SECONDS) return null;
  return Math.min(rounded, MAX_TIMER_SECONDS);
}
